import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { callStatusTracker } from '../services/callService';
import type { CallStatusResponse } from '../services/callService';
import { useUserContext } from '../store/contexts/userContext/userContexts';

/**
 * Hook that automatically invalidates call-related queries when calls complete
 * This ensures call history and communication data stays up-to-date.
 * Also sets the userId on CallStatusTracker so it can subscribe to
 * Supabase Realtime for instant call status updates.
 */
export function useCallQueryInvalidation() {
  const queryClient = useQueryClient();
  const { user } = useUserContext();
  const userId = user?.user?.id || null;

  // Keep CallStatusTracker's userId in sync for Supabase Realtime subscriptions
  useEffect(() => {
    callStatusTracker.setUserId(userId);
  }, [userId]);

  useEffect(() => {
    const handleCallComplete = async (callId: string, status: CallStatusResponse) => {

      // Invalidate all call-related queries
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['lead-call-history'] }),
        queryClient.invalidateQueries({ queryKey: ['call-history'] }),
        queryClient.invalidateQueries({ queryKey: ['communication-calls'] }),
        queryClient.invalidateQueries({ queryKey: ['dashboard-calls'] }),
      ]);

    };

    // Register the callback
    callStatusTracker.onCallComplete(handleCallComplete);

    // Cleanup on unmount
    return () => {
      callStatusTracker.offCallComplete(handleCallComplete);
    };
  }, [queryClient]);
}
